import { useState, useEffect } from "react";
import { ApiClient, getApiErrorMessage } from "@/services/api";
import { Card, Button, Alert, LoadingSpinner, StatusBadge } from "@/components/shared";
import { formatDate } from "@/utils/format";
import { X, Home, DollarSign, Calendar, MessageSquareText, Phone, Mail, User, ClipboardCheck, MessageSquare } from "lucide-react";
import {
  ProvinceLabels,
  PropertyTypeLabels,
  TimelineLabels,
  type Province,
  type PropertyType,
  type Timeline,
} from "@/types";

interface Invitation {
  id: string;
  request_id: string;
  response_id: string;
  status: string;
  lead_status: string | null;
  unlock_fee_cents: number;
  invited_at: string;
  responded_at: string | null;
  expires_at: string;
  request: {
    suburb: string;
    province: string;
    property_type: string;
    price_band_min: number;
    price_band_max: number;
    bedrooms: number | null;
    timeline: string;
    seller_notes: string | null;
  };
  my_response: {
    valuation_min: number;
    valuation_max: number;
    demand_level: string;
    strategy_summary: string;
    commission_rate: number | null;
    comparable_sales: Array<{ address: string; price: number; date: string }>;
  };
  contact: {
    name?: string;
    phone?: string;
    email?: string;
    address?: string;
    consented_at: string | null;
  } | null;
  shared_contacts?: Array<{ id: string; method: string; value: string; shared_at: string }>;
}

export function getInvitationAcceptLabel({ leadsRemaining, loading }: { leadsRemaining: number | null; loading: boolean }): string {
  if (loading) return "";
  return leadsRemaining === null || leadsRemaining <= 0 ? "Accept & Pay" : "Accept";
}

interface InvitationInboxProps {
  onInvitationsChanged?: () => Promise<void> | void;
}

export function InvitationInbox({ onInvitationsChanged }: InvitationInboxProps = {}) {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [leadsRemaining, setLeadsRemaining] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Invitation | null>(null);

  const fetchInvitations = async () => {
    try {
      const api = new ApiClient();
      const data = await api.get<{ data: Invitation[] }>("/agent-invitations");
      setInvitations(data.data);
      const subscriptionData = await api.get<{ subscription: { leads_remaining: number } | null }>("/subscriptions/me").catch(() => null);
      setLeadsRemaining(subscriptionData?.subscription?.leads_remaining ?? null);
    } catch (err: any) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  const handleAction = async (id: string, action: "accept" | "decline") => {
    setActionLoading(id);
    try {
      const api = new ApiClient();
      await api.post(`/selections/${id}/${action}`, {});
      setViewing(null);
      await fetchInvitations();
      await onInvitationsChanged?.();
    } catch (err: any) {
      setError(getApiErrorMessage(err));
    } finally {
      setActionLoading(null);
    }
  };

  const formatCurrency = (cents: number) =>
    new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(cents / 100);

  const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date();

  if (loading) return <LoadingSpinner />;

  const pending = invitations.filter((i) => i.status === "pending" && !isExpired(i.expires_at));
  const past = invitations.filter((i) => i.status !== "pending" || isExpired(i.expires_at));

  return (
    <>
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-white">Invitations ({pending.length} pending)</h2>

      {error && <Alert type="error">{error}</Alert>}

      {pending.length === 0 ? (
        <Card>
          <p className="text-white/50 text-center py-4">No pending invitations</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {pending.map((inv) => (
            <Card
              key={inv.id}
              className="border-l-4 border-l-sun cursor-pointer hover:bg-slateblue/60 transition-colors"
              onClick={() => setViewing(inv)}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-white">
                    {inv.request.suburb}, {ProvinceLabels[inv.request.province as Province] || inv.request.province.replace("_", " ")}
                  </h3>
                  <p className="text-sm text-white/50">
                    {PropertyTypeLabels[inv.request.property_type as PropertyType] || inv.request.property_type.replace("_", " ")} •{" "}
                    {formatCurrency(inv.request.price_band_min)} – {formatCurrency(inv.request.price_band_max)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/50">Unlock fee</p>
                  <p className="font-bold text-sun">
                    {leadsRemaining !== null && leadsRemaining > 0 ? "Included" : formatCurrency(inv.unlock_fee_cents)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-white/50">
                <span>Timeline: {TimelineLabels[inv.request.timeline as Timeline] || inv.request.timeline.replace("_", " ")}</span>
                <span>•</span>
                <span>Expires: {formatDate(inv.expires_at)}</span>
                <span className="ml-auto text-sun text-xs">View details →</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {past.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-white/50 mb-3">Past Invitations</h3>
          <div className="space-y-4">
            {past.map((inv) => {
              const effectiveStatus = isExpired(inv.expires_at) && inv.status === "pending" ? "expired" : inv.status;
              const borderColor = effectiveStatus === "accepted" ? "border-l-mint" : effectiveStatus === "declined" ? "border-l-coral" : "border-l-white/20";
              return (
                <Card
                  key={inv.id}
                  className={`border-l-4 ${borderColor} cursor-pointer hover:bg-slateblue/40 transition-colors`}
                  onClick={() => setViewing(inv)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-white">
                        {inv.request.suburb}, {ProvinceLabels[inv.request.province as Province] || inv.request.province.replace("_", " ")}
                      </h3>
                      <p className="text-sm text-white/50">
                        {PropertyTypeLabels[inv.request.property_type as PropertyType] || inv.request.property_type.replace("_", " ")} •{" "}
                        {formatCurrency(inv.request.price_band_min)} – {formatCurrency(inv.request.price_band_max)}
                      </p>
                    </div>
                    <StatusBadge
                      status={effectiveStatus}
                      variant={effectiveStatus === "accepted" ? "success" : effectiveStatus === "declined" ? "error" : "default"}
                    />
                  </div>

                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <span>Timeline: {TimelineLabels[inv.request.timeline as Timeline] || inv.request.timeline.replace("_", " ")}</span>
                    {inv.responded_at && (
                      <>
                        <span>•</span>
                        <span>{effectiveStatus === "accepted" ? "Accepted" : effectiveStatus === "declined" ? "Declined" : "Expired"}: {formatDate(inv.responded_at)}</span>
                      </>
                    )}
                    {effectiveStatus === "accepted" && inv.contact && (
                      <span className="ml-auto text-mint text-xs">Contact unlocked</span>
                    )}
                    {effectiveStatus !== "accepted" && (
                      <span className="ml-auto text-white/30 text-xs">View details →</span>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>

    {/* Detail overlay */}
    {viewing && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setViewing(null)}>
        <div className="bg-midnight border border-white/10 rounded-xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-white/10">
            <div>
              <h2 className="text-lg font-bold text-white">
                {viewing.request.suburb}, {ProvinceLabels[viewing.request.province as Province] || viewing.request.province}
              </h2>
              <p className="text-sm text-white/50 mt-0.5">
                Invited {formatDate(viewing.invited_at)}
              </p>
            </div>
            <button onClick={() => setViewing(null)} className="text-white/40 hover:text-white p-1">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Request details */}
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slateblue/50 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-white/50 text-xs mb-1">
                  <Home className="h-3.5 w-3.5" /> Property
                </div>
                <p className="text-white text-sm font-medium">
                  {PropertyTypeLabels[viewing.request.property_type as PropertyType] || viewing.request.property_type}
                  {viewing.request.bedrooms ? ` • ${viewing.request.bedrooms} bed` : ""}
                </p>
              </div>
              <div className="bg-slateblue/50 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-white/50 text-xs mb-1">
                  <DollarSign className="h-3.5 w-3.5" /> Price Band
                </div>
                <p className="text-white text-sm font-medium">
                  {formatCurrency(viewing.request.price_band_min)} – {formatCurrency(viewing.request.price_band_max)}
                </p>
              </div>
              <div className="bg-slateblue/50 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-white/50 text-xs mb-1">
                  <Calendar className="h-3.5 w-3.5" /> Timeline
                </div>
                <p className="text-white text-sm font-medium">
                  {TimelineLabels[viewing.request.timeline as Timeline] || viewing.request.timeline}
                </p>
              </div>
              <div className="bg-slateblue/50 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-white/50 text-xs mb-1">
                  <DollarSign className="h-3.5 w-3.5" /> Unlock Fee
                </div>
                <p className="text-sun text-sm font-bold">
                  {leadsRemaining !== null && leadsRemaining > 0 ? "Uses 1 subscription lead" : formatCurrency(viewing.unlock_fee_cents)}
                </p>
              </div>
            </div>

            {viewing.request.seller_notes && (
              <div className="bg-slateblue/50 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-white/50 text-xs mb-1">
                  <MessageSquareText className="h-3.5 w-3.5" /> Seller Notes
                </div>
                <p className="text-white/70 text-sm">{viewing.request.seller_notes}</p>
              </div>
            )}

            {/* Your response summary */}
            <div className="border-t border-white/10 pt-4">
              <h3 className="text-sm font-semibold text-white/70 mb-3">Your Response</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/50">Your Valuation</span>
                  <span className="text-white font-medium">
                    {formatCurrency(viewing.my_response.valuation_min)} – {formatCurrency(viewing.my_response.valuation_max)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Demand Level</span>
                  <span className="text-white capitalize">{viewing.my_response.demand_level}</span>
                </div>
                {viewing.my_response.commission_rate && (
                  <div className="flex justify-between">
                    <span className="text-white/50">Commission</span>
                    <span className="text-white">{viewing.my_response.commission_rate}%</span>
                  </div>
                )}
                <p className="text-white/60 text-xs mt-2">{viewing.my_response.strategy_summary}</p>
              </div>
            </div>

            {/* Shared contact details */}
            {viewing.shared_contacts && viewing.shared_contacts.length > 0 && (
              <div className="border-t border-mint/30 pt-4">
                <h3 className="text-sm font-semibold text-mint mb-3 flex items-center gap-1.5">
                  <ClipboardCheck className="h-3.5 w-3.5" /> Owner Shared Contact Details
                </h3>
                <div className="bg-mint/10 border border-mint/20 rounded-lg p-4 space-y-2">
                  {viewing.shared_contacts.map((sc) => (
                    <div key={sc.id} className="flex items-center gap-2">
                      {sc.method === "phone" && <Phone className="h-4 w-4 text-mint" />}
                      {sc.method === "email" && <Mail className="h-4 w-4 text-mint" />}
                      {sc.method === "whatsapp" && <MessageSquare className="h-4 w-4 text-mint" />}
                      <a
                        href={sc.method === "email" ? `mailto:${sc.value}` : sc.method === "whatsapp" ? `https://wa.me/${sc.value.replace(/\D/g, "")}` : `tel:${sc.value}`}
                        className="text-mint hover:underline"
                        target={sc.method === "whatsapp" ? "_blank" : undefined}
                        rel={sc.method === "whatsapp" ? "noopener noreferrer" : undefined}
                      >
                        {sc.value}
                      </a>
                      <span className="text-xs text-white/30 ml-auto">Shared {formatDate(sc.shared_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legacy QR contact details */}
            {viewing.contact && (
              <div className="border-t border-mint/30 pt-4">
                <h3 className="text-sm font-semibold text-mint mb-3 flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" /> Seller Contact Details
                </h3>
                <div className="bg-mint/10 border border-mint/20 rounded-lg p-4 space-y-2">
                  {viewing.contact.name && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-mint" />
                      <span className="text-white font-medium">{viewing.contact.name}</span>
                    </div>
                  )}
                  {viewing.contact.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-mint" />
                      <a href={`tel:${viewing.contact.phone}`} className="text-mint hover:underline">{viewing.contact.phone}</a>
                    </div>
                  )}
                  {viewing.contact.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-mint" />
                      <a href={`mailto:${viewing.contact.email}`} className="text-mint hover:underline">{viewing.contact.email}</a>
                    </div>
                  )}
                  {viewing.contact.consented_at && (
                    <p className="text-xs text-white/30 mt-2">
                      Consent granted: {formatDate(viewing.contact.consented_at)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Lead status indicator */}
            {viewing.status === "accepted" && (
              <div className="border-t border-white/10 pt-4">
                {viewing.lead_status === "contact_shared" || (viewing.shared_contacts && viewing.shared_contacts.length > 0) ? (
                  <div className="bg-mint/10 border border-mint/20 rounded-lg p-3 text-center">
                    <p className="text-sm text-mint font-medium flex items-center justify-center gap-1.5">
                      <ClipboardCheck className="h-4 w-4" /> Contact details shared
                    </p>
                    <p className="text-xs text-white/40 mt-1">You can view contact details above and reach out to the owner</p>
                  </div>
                ) : viewing.lead_status === "engaging" ? (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                    <p className="text-sm text-green-400 font-medium">Engaging with owner</p>
                    <p className="text-xs text-white/40 mt-1">Use Messages on the Active Leads page to communicate</p>
                  </div>
                ) : (
                  <div className="bg-sun/10 border border-sun/20 rounded-lg p-3 text-center">
                    <p className="text-sm text-sun font-medium">Awaiting owner response</p>
                    <p className="text-xs text-white/40 mt-1">The owner will share contact details when ready</p>
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-white/30">
              {viewing.status === "pending"
                ? `Expires: ${formatDate(viewing.expires_at)}`
                : `Responded: ${viewing.responded_at ? formatDate(viewing.responded_at) : "—"}`}
            </p>
          </div>

          {/* Actions — only for pending */}
          {viewing.status === "pending" && !isExpired(viewing.expires_at) ? (
            <div className="flex gap-3 p-5 border-t border-white/10">
              <Button
                variant="secondary"
                onClick={() => handleAction(viewing.id, "decline")}
                disabled={actionLoading === viewing.id}
                className="flex-1"
              >
                Decline
              </Button>
              <Button
                variant="primary"
                onClick={() => handleAction(viewing.id, "accept")}
                disabled={actionLoading === viewing.id}
                className="flex-1"
              >
                {actionLoading === viewing.id ? <LoadingSpinner size="sm" /> : getInvitationAcceptLabel({ leadsRemaining, loading: false })}
              </Button>
            </div>
          ) : (
            <div className="p-5 border-t border-white/10">
              <Button variant="secondary" onClick={() => setViewing(null)} className="w-full">
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
}
